/**
 * ReminderCard Component - T131
 *
 * Card display for a single payment reminder
 */
import { Link } from '@tanstack/react-router';
import { GlassPanel, Button } from '../ui';
import { formatCurrency, formatDate } from '@armard/shared';
import {
  type PaymentReminder,
  reminderLevelLabels,
  reminderLevelColors,
  downloadReminderPdf,
} from '../../hooks/useReminders';

interface ReminderCardProps {
  reminder: PaymentReminder;
  onDelete?: (id: string) => void;
  showInvoiceInfo?: boolean;
}

export function ReminderCard({ reminder, onDelete, showInvoiceInfo = false }: ReminderCardProps) {
  const levelLabel = reminderLevelLabels[reminder.reminderLevel] || `${reminder.reminderLevel}. Mahnung`;
  const levelColor = reminderLevelColors[reminder.reminderLevel] || reminderLevelColors[1];

  return (
    <GlassPanel className="p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          {/* Header */}
          <div className="flex items-center gap-3 mb-2">
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${levelColor}`}>
              {levelLabel}
            </span>
            <span className="text-white/40 text-sm">{formatDate(reminder.reminderDate)}</span>
          </div>

          {/* Invoice info */}
          {showInvoiceInfo && reminder.invoice && (
            <Link
              to="/invoices/$id"
              params={{ id: reminder.invoice.id }}
              className="text-sm text-white/70 hover:text-white transition-colors mb-2 inline-block"
            >
              Rechnung {reminder.invoice.invoiceNumber}
              {reminder.invoice.customer && ` - ${reminder.invoice.customer.companyName}`}
              <span aria-hidden="true" className="ml-1 text-white/40">→</span>
            </Link>
          )}
          {showInvoiceInfo && !reminder.invoice && (
            <p className="text-sm text-white/30 italic mb-2">Rechnung gelöscht</p>
          )}

          {/* Amounts */}
          <div className="space-y-1 text-sm">
            {parseFloat(reminder.reminderFee) > 0 && (
              <div className="flex justify-between text-white/60">
                <span>Mahngebühr:</span>
                <span>{formatCurrency(parseFloat(reminder.reminderFee))}</span>
              </div>
            )}
            {parseFloat(reminder.interestAmount) > 0 && (
              <div className="flex justify-between text-white/60">
                <span>Verzugszinsen:</span>
                <span>{formatCurrency(parseFloat(reminder.interestAmount))}</span>
              </div>
            )}
            <div className="flex justify-between text-white font-medium pt-1 border-t border-white/10">
              <span>Gesamtforderung:</span>
              <span>{formatCurrency(parseFloat(reminder.totalDue))}</span>
            </div>
          </div>

          {/* Due date */}
          <p className="text-xs text-white/40 mt-2">
            Zahlbar bis: {formatDate(reminder.dueDate)}
          </p>

          {/* Notes */}
          {reminder.notes && (
            <p className="text-sm text-white/50 mt-2 italic">{reminder.notes}</p>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => downloadReminderPdf(reminder.id)}
            title="PDF herunterladen"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </Button>
          {onDelete && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDelete(reminder.id)}
              title="Löschen"
            >
              <svg className="w-4 h-4 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            </Button>
          )}
        </div>
      </div>

      {/* PDF generated indicator */}
      {reminder.pdfGeneratedAt && (
        <p className="text-xs text-white/30 mt-2">
          PDF erstellt: {formatDate(reminder.pdfGeneratedAt)}
        </p>
      )}
    </GlassPanel>
  );
}
